import { store, type MeasureMode } from '../app/store';
import type { MeasurementManager } from '../measure/measurementManager';

/**
 * Thin DOM overlay (no framework) mounted into #ui. Drives the store; the Cesium
 * Viewer stays outside any render tree.
 */
export function mountOverlay(measure: MeasurementManager): void {
  const root = document.getElementById('ui');
  if (!root) return;

  root.innerHTML = `
    <div class="panel">
      <h1>RailTwin</h1>
      <div class="group">
        <button data-measure="distance" title="Measure distance along terrain">📏 Distance</button>
        <button data-measure="height" title="Measure height difference between two points">📐 Height</button>
        <button data-measure="area" title="Measure area (left-click vertices, right-click to close)">⬠ Area</button>
        <button data-measure="none" title="Stop measuring">✋ Off</button>
        <button data-clear title="Clear measurements">🗑 Clear</button>
      </div>
      <div class="group" id="layers"></div>
      <div class="status" id="status"></div>
    </div>
  `;

  root.querySelectorAll<HTMLButtonElement>('button[data-measure]').forEach((btn) => {
    btn.addEventListener('click', () =>
      store.set({ measureMode: btn.dataset.measure as MeasureMode }),
    );
  });
  root.querySelector<HTMLButtonElement>('button[data-clear]')
    ?.addEventListener('click', () => measure.clear());

  // Only rail is backed by real primitives today; the rest are M7/M8 stubs and
  // are shown disabled so the panel stays honest about what works.
  const layers = root.querySelector('#layers')!;
  const comingSoon = new Set(['buildings', 'roads', 'vegetation']);
  (['rail', 'buildings', 'roads', 'vegetation'] as const).forEach((name) => {
    const id = `layer-${name}`;
    const disabled = comingSoon.has(name);
    layers.insertAdjacentHTML(
      'beforeend',
      `<label class="${disabled ? 'coming-soon' : ''}" ${disabled ? 'title="Coming soon"' : ''}>
        <input type="checkbox" id="${id}" ${disabled ? 'disabled' : ''} /> ${name}${disabled ? ' <span class="soon">soon</span>' : ''}
      </label>`,
    );
    const input = layers.querySelector<HTMLInputElement>(`#${id}`)!;
    input.addEventListener('change', () => store.setLayer(name, input.checked));
  });

  store.subscribe((s) => {
    root.querySelectorAll<HTMLButtonElement>('button[data-measure]').forEach((b) => {
      b.classList.toggle('active', b.dataset.measure === s.measureMode);
    });
    (['rail', 'buildings', 'roads', 'vegetation'] as const).forEach((name) => {
      const input = root.querySelector<HTMLInputElement>(`#layer-${name}`);
      if (input) input.checked = s.layers[name];
    });
    const status = root.querySelector('#status');
    if (status) status.textContent = s.status;
  });
}
